import styled, { useTheme } from "styled-components/native";
import React, { useId, useCallback } from "react";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { Modal, Platform, TouchableOpacity } from "react-native";
import { WebPopUpProps } from "./types";
import { View } from "@gluestack-ui/themed";
import { Spacer, VDBText } from "..";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { ScrollView } from "react-native";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { PortalHost } from "@gorhom/portal";
import { css } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import { useFocusTrap } from "@hooks/accessibility";

const WebPopup = ({
  children,
  onClose,
  isOpen,
  title,
  size = "sm",
  widthPercent,
  backgroundColor,
  headerBackgroundColor,
  headerTextColor,
  hideHeader,
  transparent = true,
  noPadding,
  height = "auto",
  showScrollbar = true,
  isJBT = false,
  disableScrollWeb = false,
  style,
  skipKeyboardAwareWeb = false,
  hideClearAllButton = true,
  handleClearAll = () => {},
  leftIcon,
  leftIconPress,
  centerTitle = false,
}: WebPopUpProps) => {
  const handleCloseModal = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const theme = useTheme();
  const typography = useTypographyStore();

  // Generate unique ID for ARIA labeling
  const modalId = useId();
  const titleId = `${modalId}-title`;

  // Focus trap for keyboard accessibility
  const { containerRef, handleKeyDown } = useFocusTrap({
    isActive: isOpen,
    onEscape: handleCloseModal,
    autoFocus: true,
  });

  // Web-specific accessibility props
  const webAccessibilityProps =
    Platform.OS === "web"
      ? {
          role: "dialog" as const,
          "aria-modal": true,
          "aria-labelledby": !hideHeader && title ? titleId : undefined,
        }
      : {};

  return (
    <Modal
      animationType="fade"
      transparent={transparent}
      visible={isOpen}
      {...(Platform.OS === "ios" &&
        theme.isTablet && { supportedOrientations: ["landscape", "portrait"] })}
    >
      <Overlay softShadow="1">
        <ContainerView>
          <BasicPopup
            size={size}
            height={height}
            style={style}
            ref={containerRef as any}
            onKeyDown={Platform.OS === "web" ? handleKeyDown : undefined}
            {...webAccessibilityProps}
            widthPercent={widthPercent}
          >
            {!hideHeader && (
              <PopupHeader backgroundColor={headerBackgroundColor}>
                {!hideClearAllButton ? (
                  <TouchableOpacity onPress={handleClearAll}>
                    <VDBText
                      type={typography["Body-M"]}
                      color={theme["link-static"]}
                    >
                      Clear All
                    </VDBText>
                  </TouchableOpacity>
                ) : (
                  <Spacer size={SpaceType.Basic} />
                )}
                {leftIcon && (
                  <TouchableOpacity onPress={leftIconPress}>
                    <VDBIcon
                      name={leftIcon}
                      variant={IconsVariant.VDBCustom}
                      size={24}
                      color={headerTextColor ?? theme["core-06"]}
                    />
                  </TouchableOpacity>
                )}
                {!leftIcon && <Spacer size={SpaceType.Basic} />}
                {centerTitle ? (
                  <CenteredTitle pointerEvents="none">
                    <VDBText
                      type={typography["H2-M"]}
                      color={headerTextColor ?? theme["core-05"]}
                      textAlign={"center"}
                      nativeID={titleId}
                    >
                      {title}
                    </VDBText>
                  </CenteredTitle>
                ) : (
                  <VDBText
                    type={typography["H2-M"]}
                    color={headerTextColor ?? theme["core-05"]}
                    textAlign={"center"}
                    nativeID={titleId}
                  >
                    {title}
                  </VDBText>
                )}
                <TouchableOpacity 
                  onPress={handleCloseModal}
                  accessibilityRole="button"
                  accessibilityLabel="Close modal">
                  <VDBIcon
                    name={VDBIcons.CLOSE_CIRCLE}
                    variant={IconsVariant.VDBCustom}
                    size={24}
                    color={headerTextColor ?? theme["core-06"]}
                  />
                </TouchableOpacity>
              </PopupHeader>
            )}
            <PopupBody backgroundColor={backgroundColor} noPadding={noPadding}>
              {skipKeyboardAwareWeb ? (
                <FormView
                  isJBT={isJBT}
                  skipKeyboardAwareWeb={skipKeyboardAwareWeb}
                >
                  <FullModal>{children}</FullModal>
                </FormView>
              ) : (
                <ScrollView
                  showsVerticalScrollIndicator={showScrollbar}
                  showsHorizontalScrollIndicator={showScrollbar}
                  keyboardShouldPersistTaps={"handled"}
                  automaticallyAdjustKeyboardInsets={true}
                  contentContainerStyle={{ flexGrow: 1 }}
                  scrollEnabled={!disableScrollWeb}
                >
                  <FormView isJBT={isJBT}>{children}</FormView>
                </ScrollView>
              )}
            </PopupBody>
            <PortalHost name={"searchFilter"} />
          </BasicPopup>
        </ContainerView>
      </Overlay>
    </Modal>
  );
};

const Overlay = styled(View)`
  flex: 1;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ContainerView = styled.View`
  padding-horizontal: 16px;
  align-items: center;
`;

const BasicPopup = styled.View<{
  size: "sm" | "md" | "lg" | number;
  widthPercent?: number;
  height?: string;
}>`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  margin: 10% 5%;
  border-radius: ${SpaceType.Basic}px;
  width: 100%;
  max-width: ${({
    size,
    widthPercent,
  }: {
    size: "sm" | "md" | "lg" | number;
    widthPercent?: number;
  }) =>
    widthPercent != null
      ? `${widthPercent}%`
      : `${
          size === "sm" ? 423 : size === "md" ? 720 : size === "lg" ? 976 : size
        }px`};
  height: ${({ height }: { height?: string }) => height ?? "auto"};
  max-height: 98vh;
`;

const PopupBody = styled.View<{
  backgroundColor?: string;
  noPadding?: boolean;
}>`
  padding: ${({ noPadding }: { noPadding?: boolean }) =>
    noPadding ? 0 : 12}px;
  background-color: ${({
    backgroundColor,
    theme,
  }: {
    backgroundColor?: string;
    theme: DefaultTheme;
  }) => backgroundColor ?? theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  flex: 1;
  flex-basis: auto;
`;

const CenteredTitle = styled.View`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  align-items: center;
  justify-content: center;
`;

const PopupHeader = styled.View<{ backgroundColor?: string }>`
  position: relative;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  border-top-left-radius: ${SpaceType.Basic}px;
  border-top-right-radius: ${SpaceType.Basic}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  background-color: ${({
    backgroundColor,
    theme,
  }: {
    backgroundColor?: string;
    theme: DefaultTheme;
  }) => backgroundColor ?? theme["core-02"]};
  padding: ${SpaceType.Double}px;
`;

const FormView = styled.View<{
  isJBT?: boolean;
  skipKeyboardAwareWeb?: boolean;
}>`
  ${({ isJBT }: { isJBT?: boolean }) =>
    !isJBT
      ? css`
          overflow: hidden;
        `
      : css`
          flex: 1;
        `}
  ${({ skipKeyboardAwareWeb }: { skipKeyboardAwareWeb?: boolean }) =>
    skipKeyboardAwareWeb &&
    css`
      flex: 1;
      flex-basis: auto;
    `}
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
`;

const FullModal = styled.View`
  height: 100%;
`;
export default WebPopup;
